import React, { useContext, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { ConfirmationDialog } from '../../../../catalyst-components/confirmation-dialog';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Select } from '../../../../catalyst-components/select';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Badge } from '../../../../catalyst-components/badge';
import {
  ArrowLeftIcon,
  TrashIcon,
  EyeIcon,
  BugAntIcon,
  LightBulbIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

type FeedbackStatus = 'submitted' | 'acknowledged' | 'in_progress' | 'resolved';
type FeedbackType = 'bug' | 'feature_request' | 'general';

interface Feedback {
  _id: Id<"feedback">;
  user_id: Id<"users">;
  type: FeedbackType;
  subject: string;
  message: string;
  status: FeedbackStatus;
  created_at: string;
  updated_at: string;
  admin_notes?: string;
  resolved_by?: Id<"users">;
  resolved_at?: string;
  user_name?: string;
  user_email?: string;
}

const statusColors: Record<FeedbackStatus, "yellow" | "blue" | "purple" | "green"> = {
  submitted: 'yellow',
  acknowledged: 'blue',
  in_progress: 'purple',
  resolved: 'green',
};

const typeIcons: Record<FeedbackType, React.ReactNode> = {
  bug: <BugAntIcon className="h-4 w-4" />,
  feature_request: <LightBulbIcon className="h-4 w-4" />,
  general: <ChatBubbleLeftIcon className="h-4 w-4" />,
};

export const AdminFeedback: React.FC = () => {
  const navigate = useNavigate();
  const { convexUserId } = useContext(BaseContext);

  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | 'all'>('all');
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; feedback?: Feedback }>({ open: false });

  const allFeedback = useQuery(api.feedback.list) as Feedback[] | undefined;
  const counts = useQuery(api.feedback.getCounts);
  const updateStatus = useMutation(api.feedback.updateStatus);
  const deleteFeedback = useMutation(api.feedback.remove);

  const feedbackList = allFeedback || [];
  const filteredFeedback = statusFilter === 'all'
    ? feedbackList
    : feedbackList.filter(f => f.status === statusFilter);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = async (feedback: Feedback, newStatus: FeedbackStatus) => {
    await updateStatus({
      id: feedback._id,
      status: newStatus,
      resolved_by: newStatus === 'resolved' ? convexUserId as Id<"users"> : undefined,
    });
  };

  const handleDelete = async () => {
    if (confirmDelete.feedback) {
      await deleteFeedback({ id: confirmDelete.feedback._id });
      setConfirmDelete({ open: false });
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-amber-50 to-amber-100/50 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={() => navigate('../')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-white border-2 border-amber-200">
                <ChatBubbleLeftIcon className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground mb-1">User Feedback</h1>
                <p className="text-base text-muted-foreground">
                  Review and manage feedback from users
                </p>
              </div>
            </div>
          </div>

          {/* Status counts */}
          {counts && (
            <div className="flex gap-4 mt-6 flex-wrap">
              <Badge color="zinc">Total: {counts.total}</Badge>
              <Badge color="yellow">Submitted: {counts.submitted}</Badge>
              <Badge color="blue">Acknowledged: {counts.acknowledged}</Badge>
              <Badge color="purple">In Progress: {counts.in_progress}</Badge>
              <Badge color="green">Resolved: {counts.resolved}</Badge>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-6">
          {/* Filter */}
          <div className="flex gap-4">
            <Field className="w-48">
              <Label>Filter by Status</Label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | 'all')}
              >
                <option value="all">All</option>
                <option value="submitted">Submitted</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </Select>
            </Field>
          </div>

          {/* Table */}
          <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
            <Table>
              <TableHead>
                <TableRow className="bg-muted/30">
                  <TableHeader>Type</TableHeader>
                  <TableHeader>User</TableHeader>
                  <TableHeader>Subject</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Created</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFeedback.map((feedback) => (
                  <TableRow key={feedback._id} className="hover:bg-muted/20">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {typeIcons[feedback.type]}
                        <span className="capitalize">{feedback.type.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{feedback.user_name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{feedback.user_email || ''}</div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate font-medium">
                      {feedback.subject}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={feedback.status}
                        onChange={(e) => handleStatusChange(feedback, e.target.value as FeedbackStatus)}
                        className="w-32"
                      >
                        <option value="submitted">Submitted</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                      </Select>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(feedback.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          color="secondary"
                          className="rounded-full"
                          onClick={() => navigate(`/admin/feedback/${feedback._id}`)}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          color="red"
                          className="rounded-full"
                          onClick={() => setConfirmDelete({ open: true, feedback })}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredFeedback.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No feedback found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={handleDelete}
        title="Delete Feedback"
        description="Are you sure you want to delete this feedback? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isCritical={true}
      />
    </div>
  );
};
